// ======================== MESSAGERIE PROFESSIONNELLE v3.1 ========================
// Version responsive optimisée pour mobile, tablette et desktop
// Mobile-first: liste seule, puis conversation plein écran
// Tablette: split view (liste + conversation)
// Desktop: vue large structurée

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Send, MessageSquare, Users, Globe, AlertTriangle, 
  Search, Pin, Reply, Check, CheckCheck, ChevronDown,
  User, ArrowLeft, Volume2, VolumeX, Loader2, Menu, X,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ======================== CONSTANTES ========================

const TEAM_COLORS = {
  cuisine: { bg: '#FFF7ED', border: '#F97316', text: '#C2410C', name: 'Cuisine' },
  salle: { bg: '#EFF6FF', border: '#3B82F6', text: '#1D4ED8', name: 'Salle' },
  bar: { bg: '#F5F3FF', border: '#8B5CF6', text: '#6D28D9', name: 'Bar' },
  direction: { bg: '#F9FAFB', border: '#6B7280', text: '#374151', name: 'Direction' },
  global: { bg: '#F0FDF4', border: '#22C55E', text: '#15803D', name: 'Tout le monde' },
  urgent: { bg: '#FEF2F2', border: '#EF4444', text: '#B91C1C', name: 'Urgent' }
};

// Son de notification
const playNotificationSound = (priority = 'normal') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    const frequencies = priority === 'urgent' ? [1000, 800, 1000, 800, 1000] : [600, 800];
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(priority === 'urgent' ? 0.5 : 0.3, audioContext.currentTime);
    frequencies.forEach((freq, i) => {
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime + i * 0.15);
    });
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + frequencies.length * 0.15 + 0.1);
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + frequencies.length * 0.15 + 0.1);
  } catch (e) {}
};

// ======================== COMPOSANT MESSAGE ========================

const MessageBubble = ({ message, onReply, currentUserId, isAdmin }) => {
  const isUrgent = message.priority === 'urgent';
  const teamColor = isUrgent ? TEAM_COLORS.urgent : TEAM_COLORS[message.sender_team] || TEAM_COLORS.salle;
  
  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div 
      className={`mb-3 ${message.is_mine ? 'ml-4 sm:ml-12' : 'mr-4 sm:mr-12'}`}
      data-testid={`message-${message.id}`}
    >
      <div
        className={`
          relative p-3 rounded-xl shadow-sm
          ${message.pinned ? 'ring-2 ring-yellow-400' : ''}
        `}
        style={{
          backgroundColor: message.is_mine ? '#DBEAFE' : teamColor.bg,
          borderLeft: `4px solid ${teamColor.border}`
        }}
      >
        {/* Badge épinglé */}
        {message.pinned && (
          <div className="absolute -top-2 right-2">
            <Badge variant="secondary" className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5">
              <Pin className="w-3 h-3" />
            </Badge>
          </div>
        )}
        
        {/* En-tête */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Avatar className="h-8 w-8 flex-shrink-0" style={{ backgroundColor: message.sender_color }}>
              <AvatarFallback className="text-white text-xs font-bold">
                {getInitials(message.sender_name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="font-semibold text-sm text-gray-900 truncate">
                  {message.sender_name}
                </span>
                <Badge 
                  variant="outline" 
                  className="text-xs py-0 px-1.5 flex-shrink-0"
                  style={{ borderColor: teamColor.border, color: teamColor.text }}
                >
                  {message.sender_role_display}
                </Badge>
              </div>
              <div className="text-xs text-gray-500 truncate">
                {message.type === 'team' && message.team && `→ ${TEAM_COLORS[message.team]?.name || message.team}`}
                {message.type === 'direct' && !message.is_mine && '→ Vous'}
                {message.type === 'direct' && message.is_mine && message.recipient_name && `→ ${message.recipient_name}`}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
            <span>{formatTime(message.created_at)}</span>
            {message.is_mine && (
              message.read_by?.length > 1 
                ? <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
                : <Check className="w-3.5 h-3.5" />
            )}
          </div>
        </div>
        
        {/* Réponse */}
        {message.reply_to_preview && (
          <div className="mb-2 pl-2 border-l-2 border-gray-300 text-xs text-gray-500 bg-white/50 rounded p-1.5">
            <span className="font-medium">{message.reply_to_preview.sender_name}:</span>{' '}
            <span className="line-clamp-1">{message.reply_to_preview.content}</span>
          </div>
        )}
        
        {/* Contenu */}
        <p className="text-sm text-gray-900 whitespace-pre-wrap break-words leading-relaxed">
          {message.content}
        </p>
        
        {/* Badge urgent */}
        {isUrgent && (
          <Badge className="mt-2 bg-red-500 text-white animate-pulse text-xs">
            <AlertTriangle className="w-3 h-3 mr-1" /> URGENT
          </Badge>
        )}
        
        {/* Actions */}
        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-200/50">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => onReply(message)}
          >
            <Reply className="w-3 h-3 mr-1" /> Répondre
          </Button>
        </div>
      </div>
    </div>
  );
};

// ======================== SIDEBAR ITEM ========================

const ConversationItem = ({ item, isActive, onClick, type }) => {
  const colors = type === 'team' 
    ? TEAM_COLORS[item.id] || TEAM_COLORS.salle
    : { bg: '#F9FAFB', border: item.team_color || '#6B7280' };

  const displayName = item.name || item.user_name || 'Sans nom';
  const memberInfo = type === 'team' 
    ? `${item.member_count || 0} membre${(item.member_count || 0) > 1 ? 's' : ''}`
    : (item.last_message || item.role_display || '');

  return (
    <button
      onClick={onClick}
      className={`
        w-full p-3 rounded-xl flex items-center gap-3 transition-all text-left
        ${isActive ? 'bg-blue-50 ring-2 ring-blue-200' : 'hover:bg-gray-50 active:bg-gray-100'}
      `}
      style={{ borderLeft: `4px solid ${colors.border}` }}
    >
      <div 
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: colors.border }}
      >
        {type === 'team' ? (
          item.id === 'global' ? <Globe className="w-5 h-5 text-white" /> : <Users className="w-5 h-5 text-white" />
        ) : (
          <span className="text-white font-bold text-sm">
            {(item.user_name || item.name || 'XX').slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm text-gray-900 truncate">
            {displayName}
          </span>
          {(item.unread_count > 0) && (
            <Badge className="bg-red-500 text-white text-xs px-1.5 py-0.5 flex-shrink-0">
              {item.unread_count}
            </Badge>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">
          {memberInfo}
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
    </button>
  );
};

// ======================== COMPOSANT PRINCIPAL ========================

const MessagingProPage = () => {
  const { user, api } = useAuth();
  
  // États principaux
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  
  // Navigation mobile
  const [mobileView, setMobileView] = useState('list'); // 'list' ou 'conversation'
  const [activeConversation, setActiveConversation] = useState(null); // { type: 'team'|'user', data: {...} }
  
  // Filtres
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Message en cours
  const [messageContent, setMessageContent] = useState('');
  const [priority, setPriority] = useState('normal');
  const [replyTo, setReplyTo] = useState(null);
  
  // UI
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showUserPicker, setShowUserPicker] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showNewMessages, setShowNewMessages] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);
  
  const isAdmin = user?.role === 'ADMIN';
  
  // Détecter la taille d'écran
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 1024);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ======================== CHARGEMENT ========================
  
  const loadStats = useCallback(async () => {
    try {
      const res = await api().get('/messaging/stats');
      setStats(res.data);
    } catch (error) {}
  }, [api]);
  
  const loadMessages = useCallback(async () => {
    try {
      let url = `/messaging/all?tab=${activeTab}&limit=100`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const res = await api().get(url);
      setMessages(res.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [api, activeTab, searchQuery]);
  
  const loadUsers = useCallback(async () => {
    try {
      const res = await api().get('/messaging/users');
      setUsers(res.data);
    } catch (error) {}
  }, [api]);
  
  const loadTeams = useCallback(async () => {
    try {
      const res = await api().get('/messaging/teams');
      setTeams(res.data);
    } catch (error) {}
  }, [api]);
  
  const loadConversations = useCallback(async () => {
    try {
      const res = await api().get('/messaging/conversations');
      setConversations(res.data);
    } catch (error) {}
  }, [api]);
  
  useEffect(() => {
    loadStats();
    loadUsers();
    loadTeams();
    loadConversations();
  }, [loadStats, loadUsers, loadTeams, loadConversations]);
  
  useEffect(() => {
    setLoading(true);
    loadMessages();
  }, [loadMessages]);
  
  // Polling
  useEffect(() => {
    const interval = setInterval(() => {
      loadMessages();
      loadStats();
    }, 5000);
    return () => clearInterval(interval);
  }, [loadMessages, loadStats]);

  // ======================== SCROLL ========================
  
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const atBottom = scrollHeight - scrollTop - clientHeight < 100;
    setIsAtBottom(atBottom);
    
    if (atBottom) {
      setShowNewMessages(false);
    }
  }, []);
  
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowNewMessages(false);
    setIsAtBottom(true);
  }, []);
  
  // Auto-scroll seulement si en bas
  useEffect(() => {
    if (isAtBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (messages.length > 0) {
      setShowNewMessages(true);
    }
  }, [messages, isAtBottom]);

  // ======================== ENVOI ========================
  
  const handleSendMessage = async () => {
    if (!messageContent.trim()) return;
    
    setSending(true);
    try {
      let endpoint, payload;
      
      if (activeConversation?.type === 'user') {
        endpoint = '/messaging/direct';
        payload = {
          recipient_id: activeConversation.data.id || activeConversation.data.user_id,
          content: messageContent.trim(),
          priority,
          reply_to_id: replyTo?.id
        };
      } else {
        endpoint = '/messaging/team';
        payload = {
          team: activeConversation?.data?.id || 'global',
          content: messageContent.trim(),
          priority,
          reply_to_id: replyTo?.id
        };
      }
      
      const res = await api().post(endpoint, payload);
      setMessages(prev => [...prev, res.data]);
      setMessageContent('');
      setReplyTo(null);
      setPriority('normal');
      
      setTimeout(scrollToBottom, 100);
      loadStats();
      
      toast.success(priority === 'urgent' ? '🚨 Message URGENT envoyé !' : 'Message envoyé');
    } catch (error) {
      toast.error('Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };
  
  const handleReply = (message) => {
    setReplyTo(message);
    textareaRef.current?.focus();
  };
  
  const handleSelectConversation = (type, data) => {
    setActiveConversation({ type, data });
    if (isMobile) {
      setMobileView('conversation');
    }
  };
  
  const handleBackToList = () => {
    setMobileView('list');
  };

  // ======================== FILTRAGE MESSAGES ========================
  
  const filteredMessages = messages.filter(msg => {
    if (!activeConversation) return true;
    
    if (activeConversation.type === 'team') {
      return msg.type === 'team' && msg.team === activeConversation.data.id;
    } else {
      const recipientId = activeConversation.data.id || activeConversation.data.user_id;
      return msg.type === 'direct' && (
        (msg.sender_id === recipientId) || (msg.recipient_id === recipientId)
      );
    }
  });

  // ======================== RENDU LISTE (SIDEBAR) ========================
  
  const renderSidebar = () => (
    <div className={`
      flex flex-col h-full bg-white
      ${isMobile ? 'w-full' : isTablet ? 'w-72' : 'w-80'}
      ${isMobile && mobileView !== 'list' ? 'hidden' : ''}
    `}>
      {/* Header */}
      <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-white" />
            <h1 className="text-lg font-bold text-white">Messagerie</h1>
          </div>
          {stats.total_unread > 0 && (
            <Badge className="bg-red-500 text-white animate-pulse">
              {stats.total_unread}
            </Badge>
          )}
        </div>
        
        {/* Recherche */}
        <div className="mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-200" />
          <Input
            placeholder="Rechercher..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-blue-200"
          />
        </div>
      </div>
      
      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {/* Équipes */}
        <div>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">
            Équipes
          </h3>
          <div className="space-y-1.5">
            {teams.map(team => (
              <ConversationItem
                key={team.id}
                item={team}
                type="team"
                isActive={activeConversation?.type === 'team' && activeConversation?.data?.id === team.id}
                onClick={() => handleSelectConversation('team', team)}
              />
            ))}
          </div>
        </div>
        
        {/* Messages privés */}
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Privés
            </h3>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => setShowUserPicker(true)}
            >
              <User className="w-3 h-3 mr-1" /> Nouveau
            </Button>
          </div>
          
          {conversations.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">
              Aucune conversation
            </p>
          ) : (
            <div className="space-y-1.5">
              {conversations.map(conv => (
                <ConversationItem
                  key={conv.id}
                  item={conv}
                  type="user"
                  isActive={activeConversation?.type === 'user' && 
                    (activeConversation?.data?.id === conv.user_id || activeConversation?.data?.user_id === conv.user_id)}
                  onClick={() => handleSelectConversation('user', conv)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ======================== RENDU CONVERSATION ========================
  
  const renderConversation = () => {
    const conversationTitle = activeConversation?.type === 'team'
      ? activeConversation.data.name
      : activeConversation?.data?.user_name || activeConversation?.data?.name || 'Conversation';
    
    const conversationColor = activeConversation?.type === 'team'
      ? TEAM_COLORS[activeConversation.data.id]?.border || '#6B7280'
      : activeConversation?.data?.team_color || activeConversation?.data?.user_color || '#6B7280';

    return (
      <div className={`
        flex flex-col h-full bg-gray-50
        ${isMobile && mobileView !== 'conversation' ? 'hidden' : 'flex-1'}
      `}>
        {/* Header conversation */}
        <div 
          className="p-3 sm:p-4 border-b bg-white shadow-sm flex items-center gap-3"
          style={{ borderBottom: `3px solid ${conversationColor}` }}
        >
          {/* Bouton retour mobile */}
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              className="flex-shrink-0 -ml-2"
              onClick={handleBackToList}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          )}
          
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: conversationColor }}
          >
            {activeConversation?.type === 'team' ? (
              activeConversation.data.id === 'global' 
                ? <Globe className="w-5 h-5 text-white" />
                : <Users className="w-5 h-5 text-white" />
            ) : (
              <span className="text-white font-bold text-sm">
                {conversationTitle?.slice(0, 2).toUpperCase()}
              </span>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h2 className="font-semibold text-gray-900 truncate">{conversationTitle}</h2>
            <p className="text-xs text-gray-500">
              {activeConversation?.type === 'team' 
                ? `${activeConversation.data.member_count} membres`
                : activeConversation?.data?.role_display || activeConversation?.data?.user_role_display}
            </p>
          </div>
          
          {/* Toggle son */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="flex-shrink-0"
          >
            {soundEnabled ? (
              <Volume2 className="w-5 h-5 text-green-500" />
            ) : (
              <VolumeX className="w-5 h-5 text-gray-400" />
            )}
          </Button>
        </div>
        
        {/* Messages */}
        <div 
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-3 sm:p-4"
        >
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : filteredMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <MessageSquare className="w-12 h-12 mb-2 opacity-50" />
              <p className="text-sm">Aucun message</p>
              <p className="text-xs">Commencez la conversation !</p>
            </div>
          ) : (
            <>
              {filteredMessages.map(message => (
                <MessageBubble
                  key={message.id}
                  message={message}
                  onReply={handleReply}
                  currentUserId={user?.id}
                  isAdmin={isAdmin}
                />
              ))}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>
        
        {/* Bouton nouveaux messages */}
        {showNewMessages && !isAtBottom && (
          <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-10">
            <Button
              onClick={scrollToBottom}
              className="bg-blue-500 hover:bg-blue-600 shadow-lg rounded-full px-4"
              size="sm"
            >
              <ChevronDown className="w-4 h-4 mr-1" />
              Nouveaux messages
            </Button>
          </div>
        )}
        
        {/* Zone de saisie */}
        <div className="p-3 sm:p-4 border-t bg-white">
          {/* Réponse en cours */}
          {replyTo && (
            <div className="mb-2 p-2 bg-gray-50 rounded-lg flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">Répondre à {replyTo.sender_name}</p>
                <p className="text-sm text-gray-700 truncate">{replyTo.content}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={() => setReplyTo(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
          
          {/* Priorité */}
          <div className="flex items-center gap-2 mb-2">
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="w-28 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="important">Important</SelectItem>
                <SelectItem value="urgent" className="text-red-600 font-semibold">
                  🚨 URGENT
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Saisie */}
          <div className="flex gap-2">
            <Textarea
              ref={textareaRef}
              placeholder="Écrivez votre message..."
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              className="flex-1 min-h-[48px] max-h-[120px] resize-none text-base"
              rows={2}
              data-testid="message-input"
            />
            <Button
              onClick={handleSendMessage}
              disabled={sending || !messageContent.trim()}
              className={`h-auto px-4 sm:px-6 ${priority === 'urgent' ? 'bg-red-500 hover:bg-red-600' : ''}`}
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // ======================== RENDU PRINCIPAL ========================
  
  return (
    <div 
      className="h-[calc(100vh-60px)] sm:h-[calc(100vh-80px)] flex bg-gray-100"
      data-testid="messaging-pro-page"
    >
      {/* Sidebar */}
      {renderSidebar()}
      
      {/* Séparateur desktop/tablet */}
      {!isMobile && <div className="w-px bg-gray-200" />}
      
      {/* Conversation */}
      {(!isMobile || mobileView === 'conversation') && (
        activeConversation ? (
          renderConversation()
        ) : (
          !isMobile && (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center text-gray-400">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">Sélectionnez une conversation</p>
                <p className="text-sm">Choisissez une équipe ou un contact à gauche</p>
              </div>
            </div>
          )
        )
      )}
      
      {/* Modal sélection utilisateur */}
      <Dialog open={showUserPicker} onOpenChange={setShowUserPicker}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Nouveau message privé</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-96 space-y-1 -mx-2 px-2">
            {users.map(u => (
              <button
                key={u.id}
                onClick={() => {
                  handleSelectConversation('user', u);
                  setShowUserPicker(false);
                }}
                className="w-full p-3 rounded-lg flex items-center gap-3 hover:bg-gray-50 active:bg-gray-100 transition-colors text-left"
              >
                <Avatar className="h-10 w-10" style={{ backgroundColor: u.user_color || '#6B7280' }}>
                  <AvatarFallback className="text-white font-bold">
                    {u.name?.slice(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{u.name}</p>
                  <Badge 
                    variant="outline" 
                    className="text-xs"
                    style={{ borderColor: u.team_color, color: u.team_color }}
                  >
                    {u.role_display}
                  </Badge>
                </div>
                {u.is_online && (
                  <span className="w-2.5 h-2.5 bg-green-500 rounded-full flex-shrink-0" />
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagingProPage;
